import { useState } from "react";
import { useAdmin } from "@/context/AdminContext";

interface PasswordChangePanelProps {
  isDark: boolean;
}

export default function PasswordChangePanel({ isDark }: PasswordChangePanelProps) {
  const { getCurrentPassword, changePassword } = useAdmin();
  const [currentInput, setCurrentInput] = useState("");
  const [newInput, setNewInput] = useState("");
  const [confirmInput, setConfirmInput] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (currentInput !== getCurrentPassword()) {
      setError("Current password is incorrect.");
      return;
    }
    if (newInput.length < 4) {
      setError("New password must be at least 4 characters.");
      return;
    }
    if (newInput !== confirmInput) {
      setError("New passwords do not match.");
      return;
    }

    changePassword(newInput);
    setSuccess(true);
    setCurrentInput("");
    setNewInput("");
    setConfirmInput("");
    setTimeout(() => setSuccess(false), 4000);
  };

  const inputCls = `w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 ${
    isDark ? "border-gray-600 bg-gray-700 text-white placeholder-gray-500" : "border-gray-200 bg-white text-gray-800"
  }`;
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const headingColor = isDark ? "text-white" : "text-gray-800";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";

  return (
    <div className={`rounded-2xl border p-5 ${cardBg}`}>
      <h3 className={`font-bold text-sm mb-1 ${headingColor}`}>
        <i className="ri-key-2-line text-pink-400 mr-1.5"></i>
        Change Admin Password
      </h3>
      <p className={`text-xs mb-4 ${subColor}`}>
        Your password is stored in the cloud and synced across devices. The old password will still work as a fallback.
      </p>

      <form onSubmit={handleSubmit} className="space-y-3 max-w-md">
        <div>
          <label className={`text-xs font-semibold mb-1 block ${subColor}`}>Current Password</label>
          <div className="relative">
            <input
              type={showCurrent ? "text" : "password"}
              value={currentInput}
              onChange={(e) => setCurrentInput(e.target.value)}
              placeholder="Enter current password"
              className={`${inputCls} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className={showCurrent ? "ri-eye-off-line" : "ri-eye-line"}></i>
            </button>
          </div>
        </div>

        <div>
          <label className={`text-xs font-semibold mb-1 block ${subColor}`}>New Password</label>
          <input
            type="password"
            value={newInput}
            onChange={(e) => setNewInput(e.target.value)}
            placeholder="At least 4 characters"
            className={inputCls}
          />
        </div>

        <div>
          <label className={`text-xs font-semibold mb-1 block ${subColor}`}>Confirm New Password</label>
          <input
            type="password"
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            placeholder="Repeat new password"
            className={inputCls}
          />
        </div>

        {error && (
          <div className="text-red-400 text-xs font-semibold flex items-center gap-1">
            <i className="ri-error-warning-line"></i>
            {error}
          </div>
        )}

        {success && (
          <div className="text-green-500 text-xs font-semibold flex items-center gap-1">
            <i className="ri-check-line"></i>
            Password changed successfully! Remember to click Save in the bottom bar.
          </div>
        )}

        <button
          type="submit"
          disabled={!currentInput || !newInput || !confirmInput}
          className="bg-pink-500 hover:bg-pink-600 disabled:opacity-40 text-white text-sm font-bold px-5 py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-key-line mr-1"></i>
          Update Password
        </button>
      </form>
    </div>
  );
}